// §4.4 — INSPECTION EXECUTION. The one screen that runs an inspection, and the
// ONE implementation of it: spec 07 §4.4 says "floor PWA; office fallback
// identical", so this component is hosted by both shells and differs between
// them only in the `InspectionRunPort` it is handed (see inspection-run-port.ts)
// and in a `shell` class that changes type scale and tap targets.
//
// It lives beside module 07's other web code rather than in `components/`
// because it is a MODULE SURFACE, not a design-register primitive — it knows
// what a check item is. `components/` holds the register (§3's DataTable,
// Button, StatusChip, EvidencePhotos), and this uses those.
//
// ── THE FOUR THINGS IT MUST NOT GET WRONG ──────────────────────────────────
//
// 1. THE PINNED VERSION IS WHAT IT GRADES AGAINST (M07-S12). Every bound on
//    screen comes from `detail.check_items` — the check items of the version
//    THIS INSPECTION PINNED, which is what `GET /inspections/:id` returns. The
//    component never reads the control-point library, and the header says which
//    version it is executing, because an inspection opened on v2 must keep
//    grading against v2's bounds after v3 tightens them.
//
// 2. A MEASURED FAIL IS NEVER SHOWN AS A PASS (M07-S09, §3.4). The verdict
//    buttons come from `offeredVerdicts`, which returns ["fail"] alone for a
//    computed fail — so the control does not exist, rather than existing
//    disabled (D-118: absence, not a dead button whose reason nobody reads).
//    The server refuses the same override 422 if one were ever synthesised.
//
// 3. THE WIRE CARRIES DECIMAL STRINGS (D-041). `measured_value` is the exact
//    text the operator typed, validated against `numeric(18,6)`, and it never
//    passes through `Number()` on its way to the request. The live in/out-of-
//    range colouring is computed by the same scaled-integer comparison the
//    server grades with, so the colour and the verdict cannot disagree on a
//    boundary an operator is arguing about.
//
// 4. A QUEUED RESULT IS NOT A RECORDED ONE, AND A CONFLICTED ONE IS NEITHER
//    (D-080, §9.1). Every card carries the settlement of its own last tap:
//    applied → recorded, queued → waiting to send, conflicted → NEEDS REVIEW
//    (never a tick), needs_attention → refused, with the reason. The tick and
//    the clock are different glyphs with different words for exactly this
//    reason.
//
// THE FAILURE-ACTION SHEET (§4.4, 07-B-3 + 07-C) renders here for a failed,
// resolution-pending inspection: Rework / Scrap / Quarantine / Accept deviation
// / Guided cut, wired to the resolve and manual-hold routes through the port's
// `failureActions` arm. The sheet is ONLINE-ONLY on both shells (§4.4's dated
// sentence, extended to the cut by D-166): a decision about material consumes a
// real subject and drives modules 04/05/06 in one transaction, so it never
// rides the outbox, and offline the controls carry their reason inside the slab.
//
// 5. THE SERVER SAYS WHERE A FAULT POSITION IS TYPED (D-158, 07-C). This
//    screen's fault-position input used to render on `isMeasurement &&
//    item.uom === "m"` — a guess whose own code comment conceded it was one —
//    and `CP-CONT-FINAL`, the ONLY pack point that permits a guided cut, has
//    four items of kinds pass_fail, pass_fail, a milliamp measurement and a
//    photo. Not one is denominated in metres, so on the only control point in
//    the product where the value matters the field could not appear at all: the
//    API accepted `fault_position_m`, the column stored it, and no client could
//    produce it. The predicate is now `guided_cut.captures_fault_position` —
//    the server's own answer about the SUBJECT — narrowed to the item kinds
//    that can carry a verdict about a point on a cable (`pass_fail` and
//    `measurement`; a photo records evidence and a checklist records
//    conditions, and neither is a metre mark).

import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { normalizeLocale } from "@assessly/contracts/locales";
import { Button } from "../../../../components/button";
import { EvidencePhotos } from "../../../../components/evidence-photos";
import { Field } from "../../../../components/field";
import { RefLabel } from "../../../../components/ref-label";
import { StatusChip } from "../../../../components/status-chip";
import { formatQuantity, unitSymbol } from "../../../../lib/quantity";
import { floorPhotoProblem, MAX_FLAG_PHOTOS } from "../../../floor-pwa/lib/photo-upload";
import type { OutboxSettlement } from "../../../floor-pwa/lib/floor-outbox";
import {
  bandOf,
  completionReadiness,
  gradeMeasurement,
  isExactMeasurement,
  offeredVerdicts,
  type CompletionItemState,
  type ResultValue,
} from "../lib/grading";
import { guidedCutOf, reworkTargetWorkOrderStatusOf } from "../hooks/quality-queries";
import type {
  QualityCheckItem,
  QualityFailureCause,
  QualityInspectionDetail,
  QualityInspectionResult,
} from "../hooks/quality-queries";
import type { InspectionRunPort } from "./inspection-run-port";
import {
  FailureActionSheet,
  SheetOutcomeNote,
  type FailureActionGrants,
  type SheetOutcome,
} from "./failure-action-sheet";
import "./inspection-run.css";

/**
 * A bound, a target or a counted quantity at up to six places with the wire's
 * padding zeros dropped ("0.268", not "0.268000"). Cutting a significant digit
 * would change what the inspector is checking against, so nothing is rounded
 * below six. `undefined` for an absent or unreadable value, so the caller
 * decides how to say "no bound".
 */
function exactNumber(value: string | null | undefined, locale: string): string | undefined {
  if (value === null || value === undefined) return undefined;
  const text = formatQuantity(value, { locale, precision: 6 });
  return text.length > 0 ? text : undefined;
}

/** A unit by its symbol in the reader's language ("Ω/km", "мм"), never its
 * pack code. An absent unit is the empty string. */
function unitText(code: string | null | undefined, locale: string): string {
  const trimmed = code?.trim() ?? "";
  return trimmed.length > 0 ? unitSymbol(trimmed, { locale }) : "";
}

/** The locale a `{en,ru,zh,fr}` bundle is rendered in, falling back to `en`
 * (which the contract requires on every cause name) and then to the code, so a
 * picker never renders an empty option. */
export function localeText(
  bundle: Readonly<Record<string, string | undefined>> | null | undefined,
  language: string,
  fallback: string,
): string {
  if (bundle === null || bundle === undefined) return fallback;
  // The registry reads the tag (`zh-TW` is Traditional, never Simplified);
  // a bundle with no entry for it falls back to English.
  const locale = normalizeLocale(language) ?? "en";
  return bundle[locale] ?? bundle["en"] ?? fallback;
}

/** What the operator has entered for one item, and how their last tap ended. */
interface ItemDraft {
  readonly measured: string;
  readonly picked: ResultValue | undefined;
  readonly checklist: Readonly<Record<string, boolean>>;
  readonly faultPosition: string;
  readonly note: string;
  readonly photos: readonly File[];
  /**
   * How many photos this item has already handed to the OUTBOX.
   *
   * Evidence that is queued is evidence that EXISTS — the bytes are durable on
   * the device and will upload with the tap. Without this the count is
   * `server + draft`, and a queued tap satisfies neither: the server has not
   * seen it and `photos` was just emptied (deliberately — keeping the Files
   * would re-upload them on the next tap). A photo-required item therefore
   * read as missing its evidence for the whole airplane-mode session and
   * `Finish` stayed dead, which is the one thing a floor screen may never do.
   * Found by M07-S08 driving a real offline inspection.
   */
  readonly queuedPhotos: number;
  readonly photoProblem: string | undefined;
  /** The settlement of the last recorded tap for this item, if any. */
  readonly settlement: OutboxSettlement["outcome"] | undefined;
  readonly conflictReason: string | undefined;
  /** The verdict the SERVER computed and echoed back, when it answered. */
  readonly appliedResult: ResultValue | undefined;
  /** True once a tap has been queued — the value on screen is the truth of
   * record on this device even though the server has not seen it. */
  readonly queuedResult: ResultValue | undefined;
}

const EMPTY_DRAFT: ItemDraft = {
  measured: "",
  picked: undefined,
  checklist: {},
  faultPosition: "",
  note: "",
  photos: [],
  queuedPhotos: 0,
  photoProblem: undefined,
  settlement: undefined,
  conflictReason: undefined,
  appliedResult: undefined,
  queuedResult: undefined,
};

function checklistRows(item: QualityCheckItem): { key: string; labels: Record<string, string> }[] {
  const raw = item.checklist_items_i18n;
  if (raw === null) return [];
  const rows: { key: string; labels: Record<string, string> }[] = [];
  for (const entry of raw) {
    const record = entry as Record<string, unknown>;
    const key = record["key"];
    if (typeof key !== "string" || key.length === 0) continue;
    const label = record["label"];
    rows.push({
      key,
      labels: label !== null && typeof label === "object" ? (label as Record<string, string>) : {},
    });
  }
  return rows;
}

export interface InspectionRunProps {
  readonly detail: QualityInspectionDetail;
  readonly causes: readonly QualityFailureCause[];
  /** `quality.inspection.record` — §7. Absent ⇒ the whole recording surface is
   * absent, not disabled (D-118): a planner opening an inspection reads it. */
  readonly canRecord: boolean;
  /** The §4.4 failure-action sheet's grants (07-B-3), derived by both hosts
   * from the ONE mirror in hooks/permissions.ts. A grant absent ⇒ its button
   * ABSENT (D-118); gated separately from `canRecord` because deciding what
   * happens to failed material is not the recording act — a qc lead may
   * dispose an inspection somebody else recorded. */
  readonly failureGrants: FailureActionGrants;
  readonly port: InspectionRunPort;
  readonly shell: "floor" | "office";
  /** Called after a settlement that could have changed server state — or,
   * for the §4.4 sheet, after a REFUSAL, whose whole meaning is that the
   * server holds a truth this screen may not (R1: the lost-response retry
   * replays into a refusal over an action that already succeeded, and only a
   * re-read swaps the stale pending sheet for the resolved line). Never
   * called for a `queued` settlement — there is nothing new on the server to
   * read, and refetching offline would only paint the pre-tap snapshot over
   * the operator's own entry. */
  readonly onApplied?: () => void;
}

export function InspectionRun({
  detail,
  causes,
  canRecord,
  failureGrants,
  port,
  shell,
  onApplied,
}: InspectionRunProps) {
  const { t, i18n } = useTranslation();
  const inspection = detail.inspection;
  // §3.5d's server-computed block (D-158). Read through an optional index
  // rather than a dereference: the floor's inspection read is a versionless
  // `useCachedRead` snapshot, so a device that cached this inspection before
  // 07-C landed has a payload with no `guided_cut` — and a TypeError here is a
  // white screen at the drum, offline, which is the one failure mode the floor
  // half of this module exists to prevent. Its absence is rendered as a stated
  // fact by the sheet, never as a silent absence.
  const guidedCut = guidedCutOf(detail);
  // D-299 ruling G5: the order a rework would re-open, read through the same
  // tolerant accessor — a pre-D-299 cached floor snapshot has no key, and that
  // absence renders Rework live exactly as before (spec 07 §4.4).
  const reworkTargetWorkOrderStatus = reworkTargetWorkOrderStatusOf(detail);
  const items = useMemo(
    () => [...detail.check_items].sort((a, b) => a.seq - b.seq),
    [detail.check_items],
  );
  const [drafts, setDrafts] = useState<Readonly<Record<string, ItemDraft>>>({});
  const [causeId, setCauseId] = useState("");
  const [causeSearch, setCauseSearch] = useState("");
  const [notes, setNotes] = useState("");
  const [completion, setCompletion] = useState<OutboxSettlement | undefined>(undefined);
  // The last §4.4 action's outcome, held HERE so the refs (the re-opened
  // operation, the waste event, the quarantine number, the labelled scrap
  // valuation) survive the refetch that replaces the pending sheet with the
  // resolved line.
  const [sheetOutcome, setSheetOutcome] = useState<SheetOutcome | undefined>(undefined);

  const draftOf = (id: string): ItemDraft => drafts[id] ?? EMPTY_DRAFT;
  const patch = (id: string, next: Partial<ItemDraft>): void => {
    setDrafts((prior) => ({ ...prior, [id]: { ...(prior[id] ?? EMPTY_DRAFT), ...next } }));
  };

  // The server's own rows, indexed. A result the server has is the truth for
  // everything the operator has not touched since.
  const serverResults = useMemo(() => {
    const map = new Map<string, QualityInspectionResult>();
    for (const row of detail.results) map.set(row.check_item_id, row);
    return map;
  }, [detail.results]);

  // Seed the drafts from the server ONCE per inspection, so an operator who
  // returns to a half-finished inspection (the §2.6 eager-creation contract is
  // exactly what makes that possible) sees their own numbers rather than empty
  // fields. Keyed on the inspection id and the result count: a re-read that
  // brings a NEW row seeds it, and one that brings the same rows leaves the
  // operator's in-flight typing alone.
  const seeded = useRef<string>("");
  useEffect(() => {
    const key = `${inspection.id}:${String(detail.results.length)}`;
    if (seeded.current === key) return;
    seeded.current = key;
    setDrafts((prior) => {
      const next = { ...prior };
      for (const row of detail.results) {
        const existing = next[row.check_item_id] ?? EMPTY_DRAFT;
        next[row.check_item_id] = {
          ...existing,
          measured: existing.measured.length > 0 ? existing.measured : (row.measured_value ?? ""),
          faultPosition:
            existing.faultPosition.length > 0
              ? existing.faultPosition
              : (row.fault_position_m ?? ""),
          appliedResult: row.result ?? undefined,
        };
      }
      return next;
    });
  }, [inspection.id, detail.results]);

  const isTerminal =
    inspection.status === "passed" ||
    inspection.status === "failed" ||
    inspection.status === "cancelled";

  /** The merged verdict for an item: what the operator queued beats what the
   * server last said, because on this device the queued tap IS the record. */
  const verdictOf = (item: QualityCheckItem): ResultValue | null => {
    const draft = draftOf(item.id);
    return draft.queuedResult ?? draft.appliedResult ?? serverResults.get(item.id)?.result ?? null;
  };

  const completionState: CompletionItemState[] = items.map((item) => {
    const draft = draftOf(item.id);
    const stored = serverResults.get(item.id);
    return {
      checkItemId: item.id,
      // The BUNDLE, resolved (#128). This name is not decoration: it is what
      // `blockedMissing` / `blockedEvidence` list back at the operator when
      // Finish is held, so an English-only string here would answer a Russian
      // inspector's "why can't I finish" in a language they do not read.
      name: localeText(item.name_i18n, i18n.language, item.name),
      isRequired: item.is_required,
      result: verdictOf(item),
      photoRequired: item.photo_required || item.check_type === "photo",
      minPhotos: item.min_photos,
      // Server-confirmed + still-in-hand + ALREADY QUEUED. The third term is
      // what makes a photo-required item completable offline.
      photoCount: (stored?.photo_file_ids.length ?? 0) + draft.photos.length + draft.queuedPhotos,
    };
  });
  const readiness = completionReadiness(completionState);
  const recorded = completionState.filter((s) => s.result !== null).length;

  const filteredCauses = causes.filter((cause) => {
    if (causeSearch.trim().length === 0) return true;
    const needle = causeSearch.trim().toLowerCase();
    return (
      cause.code.toLowerCase().includes(needle) ||
      localeText(cause.name_i18n, i18n.language, cause.code).toLowerCase().includes(needle)
    );
  });
  const causesByCategory = new Map<string, QualityFailureCause[]>();
  for (const cause of filteredCauses) {
    const bucket = causesByCategory.get(cause.category) ?? [];
    bucket.push(cause);
    causesByCategory.set(cause.category, bucket);
  }

  const identity = `${inspection.inspection_no} · ${inspection.control_point_code}`;

  const record = async (item: QualityCheckItem): Promise<void> => {
    const draft = draftOf(item.id);
    const computed =
      item.check_type === "measurement"
        ? gradeMeasurement(draft.measured, {
            min_value: item.min_value,
            max_value: item.max_value,
          })
        : undefined;
    // The verdict the request carries. For a measurement with no override the
    // field is ABSENT and the server computes it — the one path that cannot
    // desynchronise two graders (see `verdictToSend`'s note). An override is
    // only ever a computed pass being failed by eye.
    const sendVerdict =
      item.check_type === "measurement"
        ? draft.picked !== undefined && draft.picked !== computed && draft.picked === "fail"
          ? ("fail" as const)
          : undefined
        : draft.picked;
    const settlement = await port.recordResult({
      inspectionId: inspection.id,
      body: {
        check_item_id: item.id,
        ...(sendVerdict !== undefined ? { result: sendVerdict } : {}),
        ...(item.check_type === "measurement" && draft.measured.length > 0
          ? { measured_value: draft.measured }
          : {}),
        ...(item.check_type === "checklist" ? { checklist_results: draft.checklist } : {}),
        ...(draft.faultPosition.length > 0 ? { fault_position_m: draft.faultPosition } : {}),
        ...(draft.note.trim().length > 0 ? { note: draft.note.trim() } : {}),
      },
      photos: draft.photos,
      // The OUTBOX identity line the operator sees against a queued tap, so it
      // is localised for the same reason the item row is (#128).
      identity: `${identity} · ${localeText(item.name_i18n, i18n.language, item.name)}`,
    });
    // What the operator now sees this item to be. On `applied` the SERVER's
    // echoed verdict wins — it is the one that was graded and stored. On
    // `queued` the local one does, because nothing was graded yet and showing
    // the item as unanswered would invite a second entry of the same reading.
    const applied =
      settlement.outcome === "applied" ? readEchoedVerdict(settlement.response) : undefined;
    patch(item.id, {
      settlement: settlement.outcome,
      conflictReason: settlement.outcome === "conflicted" ? settlement.reason : undefined,
      appliedResult: applied ?? draft.appliedResult,
      queuedResult:
        settlement.outcome === "queued"
          ? (sendVerdict ?? computed ?? draft.picked)
          : draft.queuedResult,
      // The bytes are the host's now (queued durably, or uploaded). Keeping the
      // File objects would re-upload them on the next tap for this item — so
      // the COUNT is carried forward instead, or a queued photo would vanish
      // from the evidence tally the moment it became durable.
      photos: settlement.outcome === "needs_attention" ? draft.photos : [],
      queuedPhotos:
        settlement.outcome === "queued"
          ? draft.queuedPhotos + draft.photos.length
          : draft.queuedPhotos,
    });
    if (settlement.outcome === "applied") onApplied?.();
  };

  const finish = async (): Promise<void> => {
    const settlement = await port.complete({
      inspectionId: inspection.id,
      body: {
        ...(readiness.willFail && causeId.length > 0 ? { failure_cause_id: causeId } : {}),
        ...(notes.trim().length > 0 ? { notes: notes.trim() } : {}),
      },
      identity,
    });
    setCompletion(settlement);
    if (settlement.outcome === "applied") onApplied?.();
  };

  const completeBlockedReason = ((): string | undefined => {
    if (readiness.missing.length > 0) {
      return t("quality.run.blockedMissing", {
        count: readiness.missing.length,
        names: readiness.missing
          .slice(0, 3)
          .map((s) => s.name)
          .join(", "),
      });
    }
    if (readiness.missingEvidence.length > 0) {
      return t("quality.run.blockedEvidence", {
        count: readiness.missingEvidence.length,
        names: readiness.missingEvidence
          .slice(0, 3)
          .map((s) => s.name)
          .join(", "),
      });
    }
    if (readiness.willFail && causeId.length === 0) return t("quality.run.blockedCause");
    return undefined;
  })();

  return (
    <section className={`qrun qrun-${shell}`} data-testid="inspection-run">
      <header className="qrun-head">
        <div className="qrun-head-main">
          <p className="qrun-no" data-testid="qrun-no">
            {inspection.inspection_no}
          </p>
          {/* The run header, on the floor tablet AND in the office (§4.4: one
              component) — resolved from the pinned version's bundle (#128). */}
          <h2 className="qrun-title">
            {localeText(
              inspection.control_point_name_i18n,
              i18n.language,
              inspection.control_point_code,
            )}
          </h2>
        </div>
        <div className="qrun-chips">
          <StatusChip variant={stageVariant(inspection.stage)}>
            {t(`quality.stage.${inspection.stage}`)}
          </StatusChip>
          <StatusChip variant={statusVariant(inspection.status)} data-testid="qrun-status">
            {t(`quality.status.${inspection.status}`)}
          </StatusChip>
          {inspection.is_blocking ? (
            <StatusChip variant="warn" data-testid="qrun-blocking">
              {t("quality.run.blocking")}
            </StatusChip>
          ) : null}
        </div>
      </header>

      {/* M07-S12 — WHICH VERSION IS BEING EXECUTED, on the screen, always. An
          inspection grades against the definition it PINNED, and after somebody
          tightens a limit the only way an inspector can tell which bounds they
          are reading is if the screen says so. */}
      <p className="qrun-pin" data-testid="qrun-version-pin">
        {t("quality.run.versionPin", {
          code: inspection.control_point_code,
          version: inspection.control_point_version,
        })}
      </p>

      <dl className="qrun-subject" data-testid="qrun-subject">
        {inspection.stock_unit_id !== null ? (
          <>
            <dt>{t("quality.run.subjectUnit")}</dt>
            <dd>
              <RefLabel kind="stock_unit" id={inspection.stock_unit_id} />
            </dd>
          </>
        ) : null}
        {inspection.lot_id !== null ? (
          <>
            <dt>{t("quality.run.subjectLot")}</dt>
            <dd>
              <RefLabel kind="lot" id={inspection.lot_id} />
            </dd>
          </>
        ) : null}
        {inspection.work_order_operation_id !== null ? (
          <>
            <dt>{t("quality.run.subjectOperation")}</dt>
            <dd>
              {/* The names read has no kind for an operation, so it stays the
                  short chip with the full id on its title. */}
              <RefLabel id={inspection.work_order_operation_id} />
            </dd>
          </>
        ) : null}
        {inspection.quantity_inspected !== null ? (
          <>
            <dt>{t("quality.run.subjectQty")}</dt>
            <dd>
              {exactNumber(inspection.quantity_inspected, i18n.language) ??
                inspection.quantity_inspected}
            </dd>
          </>
        ) : null}
      </dl>

      {isTerminal ? (
        <p className="qrun-closed" role="status" data-testid="qrun-closed">
          {t(`quality.run.closed.${inspection.status}`)}
        </p>
      ) : null}

      {/* §4.4's failure-action sheet (07-B-3): a failed inspection whose
          resolution is PENDING is decided HERE, in both shells. A resolved one
          says which §3.5 workflow closed it (the wire's resolution_kind —
          rendered, never guessed). */}
      {inspection.status === "failed" && inspection.resolution_status === "pending" ? (
        <FailureActionSheet
          inspection={inspection}
          guidedCut={guidedCut}
          reworkTargetWorkOrderStatus={reworkTargetWorkOrderStatus}
          arm={port.failureActions}
          grants={failureGrants}
          onOutcome={setSheetOutcome}
          {...(onApplied !== undefined ? { onApplied } : {})}
        />
      ) : null}
      {inspection.status === "failed" &&
      inspection.resolution_status === "resolved" &&
      inspection.resolution_kind !== null ? (
        <p className="qrun-closed" role="status" data-testid="qrun-resolved-kind">
          {t(`quality.run.resolvedAs.${inspection.resolution_kind}`)}
        </p>
      ) : null}
      {sheetOutcome !== undefined ? (
        <SheetOutcomeNote outcome={sheetOutcome} locale={i18n.language} />
      ) : null}

      {!canRecord && !isTerminal ? (
        <p className="qrun-readonly" role="status" data-testid="qrun-readonly">
          {t("quality.run.readOnly")}
        </p>
      ) : null}

      <ol className="qrun-items" data-testid="qrun-items">
        {items.map((item, index) => (
          <CheckItemCard
            key={item.id}
            item={item}
            position={index + 1}
            draft={draftOf(item.id)}
            stored={serverResults.get(item.id)}
            liveSettlement={port.liveSettlements.get(item.id)}
            // D-158: the SERVER decides whether this inspection captures a
            // fault position. A missing block (a pre-07-C cached snapshot)
            // reads as `false` — the same answer the old predicate gave on
            // every item of the only point that permits a cut, so nothing that
            // ever worked stops working, and the value is enterable again the
            // moment the device re-reads.
            capturesFaultPosition={guidedCut?.captures_fault_position ?? false}
            verdict={verdictOf(item)}
            editable={canRecord && !isTerminal}
            busy={port.busy}
            onPatch={(next) => {
              patch(item.id, next);
            }}
            onRecord={() => {
              void record(item);
            }}
          />
        ))}
      </ol>

      {canRecord && !isTerminal ? (
        <div className="qrun-foot" data-testid="qrun-foot">
          <p className="qrun-progress" data-testid="qrun-progress">
            {t("quality.run.progress", { done: recorded, total: items.length })}
          </p>

          {/* §3.4 — `failure_cause_id` is mandatory when any REQUIRED item is
              `fail`, so the picker appears exactly then and not before. A
              picker that is always on screen teaches the inspector to scroll
              past it. */}
          {readiness.willFail ? (
            <div className="qrun-cause" data-testid="qrun-cause">
              <p className="qrun-cause-lead">{t("quality.run.causeLead")}</p>
              <Field label={t("quality.run.causeSearch")} wide>
                <input
                  value={causeSearch}
                  data-testid="qrun-cause-search"
                  placeholder={t("quality.run.causeSearchHint")}
                  onChange={(e) => {
                    setCauseSearch(e.target.value);
                  }}
                />
              </Field>
              {causes.length === 0 ? (
                <p className="qrun-note" data-testid="qrun-cause-empty">
                  {t("quality.run.causeEmpty")}
                </p>
              ) : null}
              {[...causesByCategory.entries()].map(([category, rows]) => (
                <div key={category} className="qrun-cause-group">
                  <p className="qrun-cause-group-name">{t(`quality.causeCategory.${category}`)}</p>
                  <div className="qrun-cause-grid">
                    {rows.map((cause) => (
                      <button
                        key={cause.id}
                        type="button"
                        className="qrun-pick"
                        aria-pressed={causeId === cause.id}
                        data-testid="qrun-cause-option"
                        onClick={() => {
                          setCauseId(cause.id);
                        }}
                      >
                        {localeText(cause.name_i18n, i18n.language, cause.code)}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              {/* No disposition control HERE: §3.5's workflows act on a FAILED
                  inspection, and this one is still being completed. The §4.4
                  action sheet renders above the moment the failed completion
                  applies (07-B-3 — this replaced 07-A's dispositionPending
                  sentence). */}
            </div>
          ) : null}

          <Field label={t("quality.run.notes")} wide>
            <input
              value={notes}
              data-testid="qrun-notes"
              onChange={(e) => {
                setNotes(e.target.value);
              }}
            />
          </Field>

          <Button
            variant="primary"
            data-testid="qrun-complete"
            disabled={completeBlockedReason !== undefined || port.busy}
            busy={port.busy}
            busyLabel={t("quality.run.saving")}
            onClick={() => {
              void finish();
            }}
          >
            {readiness.willFail ? t("quality.run.completeFail") : t("quality.run.complete")}
          </Button>
          {/* D-118 — a dead control says WHY, at the control. */}
          {completeBlockedReason !== undefined ? (
            <p className="qrun-blocked" role="status" data-testid="qrun-complete-blocked">
              {completeBlockedReason}
            </p>
          ) : null}

          {completion !== undefined ? (
            <SettlementNote settlement={completion} testId="qrun-completion" />
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

/** The verdict the server echoed on a `POST /results` 200 (§5: "computed result
 * echoed"). `undefined` for anything that is not that shape — the caller keeps
 * what it had rather than inventing a verdict. */
function readEchoedVerdict(response: unknown): ResultValue | undefined {
  const row = (response as { result?: unknown } | null | undefined)?.result;
  if (row === null || row === undefined || typeof row !== "object") return undefined;
  const value = (row as Record<string, unknown>)["result"];
  return value === "pass" || value === "fail" || value === "na" ? value : undefined;
}

function stageVariant(stage: string): "ok" | "warn" | "bad" | "neutral" | "accent" {
  return stage === "final" ? "accent" : "neutral";
}

function statusVariant(status: string): "ok" | "warn" | "bad" | "neutral" | "accent" {
  switch (status) {
    case "passed":
      return "ok";
    case "failed":
      return "bad";
    case "in_progress":
      return "warn";
    default:
      return "neutral";
  }
}

// ---------------------------------------------------------------------------
// One check item
// ---------------------------------------------------------------------------

/**
 * The allowed range in words: "Allowed 1.9 to 2.1 mm", "At most 0.268 Ω/km",
 * with the target only when there is one. Numbers are trimmed of the
 * database's six decimal places and use the reader's separators.
 */
export function boundsText(
  t: TFunction,
  item: Pick<QualityCheckItem, "min_value" | "max_value" | "target_value" | "uom">,
  locale: string,
): string {
  const n = (v: string): string => exactNumber(v, locale) ?? v;
  const uom = unitText(item.uom, locale);
  const range =
    item.min_value !== null && item.max_value !== null
      ? t("quality.run.bounds", { min: n(item.min_value), max: n(item.max_value), uom })
      : item.max_value !== null
        ? t("quality.run.boundsAtMost", { max: n(item.max_value), uom })
        : item.min_value !== null
          ? t("quality.run.boundsAtLeast", { min: n(item.min_value), uom })
          : "";
  const target =
    item.target_value !== null
      ? t("quality.run.boundsTarget", { target: n(item.target_value), uom })
      : "";
  return [range, target].filter((x) => x !== "").join(" · ");
}

function CheckItemCard({
  item,
  position,
  draft,
  stored,
  verdict,
  editable,
  busy,
  onPatch,
  onRecord,
  liveSettlement,
  capturesFaultPosition,
}: {
  item: QualityCheckItem;
  /** Where the item sits on this inspection, counted from 1 (the stored
   * `seq` counts from 0 and is not for reading). */
  position: number;
  draft: ItemDraft;
  stored: QualityInspectionResult | undefined;
  /** What the device queue says about THIS item right now, when it still holds
   * the tap — outranks the tap-time settlement the card remembers. */
  liveSettlement: OutboxSettlement["outcome"] | undefined;
  /** `guided_cut.captures_fault_position` (D-158) — the pinned point offers a
   * cut AND the subject is a length-measured stock unit. Deliberately NOT
   * gated on `eligible`: the operator types the mark while the inspection is
   * still in progress, before there is a failure to be eligible for. */
  capturesFaultPosition: boolean;
  verdict: ResultValue | null;
  editable: boolean;
  busy: boolean;
  onPatch: (next: Partial<ItemDraft>) => void;
  onRecord: () => void;
}) {
  const { t, i18n } = useTranslation();
  const photoInput = useRef<HTMLInputElement | null>(null);
  const bounds = { min_value: item.min_value, max_value: item.max_value };
  const isMeasurement = item.check_type === "measurement";
  const band = isMeasurement ? bandOf(draft.measured, bounds) : "unknown";
  const computed = isMeasurement ? gradeMeasurement(draft.measured, bounds) : undefined;
  const verdicts = offeredVerdicts({
    checkType: item.check_type,
    bounds,
    measuredText: draft.measured,
    isRequired: item.is_required,
  });
  const rows = checklistRows(item);
  const needsPhoto = item.photo_required || item.check_type === "photo";
  const photoCount =
    (stored?.photo_file_ids.length ?? 0) + draft.photos.length + draft.queuedPhotos;
  // §3.4 — a computed PASS failed by eye is the one override that adds
  // information, and it asks for a note. A computed FAIL needs none: the
  // instrument already said why.
  const overriding = isMeasurement && computed === "pass" && draft.picked === "fail";
  const measuredValid = draft.measured.length === 0 || isExactMeasurement(draft.measured);
  const faultValid = draft.faultPosition.length === 0 || isExactMeasurement(draft.faultPosition);

  const canRecord =
    editable &&
    !busy &&
    measuredValid &&
    faultValid &&
    (isMeasurement ? draft.measured.length > 0 : draft.picked !== undefined) &&
    (!overriding || draft.note.trim().length > 0) &&
    (!needsPhoto || photoCount >= item.min_photos);

  const onPickPhotos = (files: FileList | null): void => {
    if (files === null) return;
    const picked = [...files].slice(0, MAX_FLAG_PHOTOS);
    for (const file of picked) {
      const problem = floorPhotoProblem(file);
      if (problem !== undefined) {
        onPatch({ photoProblem: problem });
        return;
      }
    }
    onPatch({ photoProblem: undefined, photos: picked });
  };

  return (
    <li className="qrun-item" data-testid="qrun-item" data-check-type={item.check_type}>
      <div className="qrun-item-head">
        <p className="qrun-item-name">
          <span className="qrun-seq">{position}</span>{" "}
          {localeText(item.name_i18n, i18n.language, item.name)}
        </p>
        <div className="qrun-item-chips">
          {!item.is_required ? (
            <StatusChip variant="neutral" dot={false}>
              {t("quality.run.optional")}
            </StatusChip>
          ) : null}
          {verdict !== null ? (
            <StatusChip
              variant={verdict === "pass" ? "ok" : verdict === "fail" ? "bad" : "neutral"}
              data-testid="qrun-item-verdict"
            >
              {t(`quality.verdict.${verdict}`)}
            </StatusChip>
          ) : null}
          {/* The QUEUE's current word wins over the tap-time one: a tap this
              screen saw settle as `queued` may since have been refused by a
              drain, and the operator standing here must see that rather than a
              clock that will never resolve (port.liveSettlements). */}
          <SyncChip settlement={liveSettlement ?? draft.settlement} />
        </div>
      </div>

      {isMeasurement ? (
        <div className="qrun-measure" data-testid="qrun-measure">
          <p className="qrun-bounds" data-testid="qrun-bounds">
            {boundsText(t, item, i18n.language)}
          </p>
          {/* The tick-mark bar §4.4 asks for: the admissible span, with the
              target marked, and the reading placed on it. Rendered only when
              BOTH bounds exist — a one-sided span has no width to draw and a
              guessed one would place the reading somewhere it is not. */}
          {item.min_value !== null && item.max_value !== null ? (
            <MeasureBar
              min={item.min_value}
              max={item.max_value}
              target={item.target_value}
              value={draft.measured}
            />
          ) : null}
          <label className="qrun-field">
            {t("quality.run.measuredLabel", { uom: unitText(item.uom, i18n.language) })}
            <input
              inputMode="decimal"
              autoComplete="off"
              className="qrun-input"
              value={draft.measured}
              disabled={!editable}
              data-testid="qrun-measure-input"
              data-band={band}
              onChange={(e) => {
                onPatch({ measured: e.target.value, picked: undefined, note: "" });
              }}
            />
          </label>
          {!measuredValid ? (
            <p className="qrun-invalid" role="alert" data-testid="qrun-measure-invalid">
              {t("quality.run.measureInvalid")}
            </p>
          ) : null}
          {computed !== undefined ? (
            <p
              className={computed === "pass" ? "qrun-computed-ok" : "qrun-computed-bad"}
              role="status"
              data-testid="qrun-computed"
              data-computed={computed}
            >
              {t(`quality.run.computed.${computed}`)}
            </p>
          ) : null}
          {/* M07-S09's other half, in words: when the instrument said fail there
              is no Pass control above, and this says why so the absence reads as
              a rule rather than as a missing button. */}
          {computed === "fail" ? (
            <p className="qrun-note" data-testid="qrun-no-override">
              {t("quality.run.noOverride")}
            </p>
          ) : null}
        </div>
      ) : null}

      {item.check_type === "checklist" ? (
        <ul className="qrun-checklist" data-testid="qrun-checklist">
          {rows.map((row) => (
            <li key={row.key}>
              <label className="qrun-check">
                <input
                  type="checkbox"
                  checked={draft.checklist[row.key] === true}
                  disabled={!editable}
                  data-testid="qrun-checklist-row"
                  onChange={(e) => {
                    onPatch({ checklist: { ...draft.checklist, [row.key]: e.target.checked } });
                  }}
                />
                <span>{localeText(row.labels, i18n.language, row.key)}</span>
              </label>
            </li>
          ))}
        </ul>
      ) : null}

      {/* §4.4's fault-position field — metres from the A-end, the ONE input the
          whole 07-C flow depends on: no fault position, no cut plan.

          THE PREDICATE IS THE SERVER'S (D-158), replacing `isMeasurement &&
          item.uom === "m"`. That heuristic rendered the field on exactly ZERO
          of the four items of `CP-CONT-FINAL` — the only pack point carrying
          `allow_guided_cut` — because none of them is denominated in metres,
          so the value the flow needs could not be entered anywhere in the
          product. `captures_fault_position` asks about the SUBJECT (a
          length-measured stock unit under a point that offers a cut), which is
          what §3.5d's eligibility is actually about, and the item-kind
          narrowing here is §4.4's: a verdict about a point on the cable is a
          `pass_fail` or a `measurement`; a `photo` records evidence and a
          `checklist` records conditions, and neither is a metre mark. */}
      {capturesFaultPosition && (isMeasurement || item.check_type === "pass_fail") ? (
        <label className="qrun-field">
          {t("quality.run.faultPosition")}
          <input
            inputMode="decimal"
            autoComplete="off"
            className="qrun-input"
            value={draft.faultPosition}
            disabled={!editable}
            data-testid="qrun-fault-position"
            onChange={(e) => {
              onPatch({ faultPosition: e.target.value });
            }}
          />
        </label>
      ) : null}

      {editable && verdicts.length > 0 ? (
        <div className="qrun-verdicts" data-testid="qrun-verdicts">
          {verdicts.map((value) => (
            <button
              key={value}
              type="button"
              className={`qrun-pick qrun-pick-${value}`}
              aria-pressed={draft.picked === value}
              data-testid={`qrun-verdict-${value}`}
              onClick={() => {
                onPatch({ picked: value });
              }}
            >
              {t(`quality.verdict.${value}`)}
            </button>
          ))}
        </div>
      ) : null}

      {overriding ? (
        <label className="qrun-field">
          {t("quality.run.overrideNote")}
          <input
            value={draft.note}
            data-testid="qrun-override-note"
            onChange={(e) => {
              onPatch({ note: e.target.value });
            }}
          />
        </label>
      ) : null}

      {needsPhoto || item.check_type === "photo" ? (
        <div className="qrun-photos" data-testid="qrun-photos">
          <input
            ref={photoInput}
            type="file"
            accept="image/*"
            capture="environment"
            multiple
            hidden
            data-testid="qrun-photo-input"
            onChange={(e) => {
              onPickPhotos(e.target.files);
            }}
          />
          {editable ? (
            <Button
              size="sm"
              data-testid="qrun-photo-btn"
              onClick={() => photoInput.current?.click()}
            >
              {draft.photos.length > 0
                ? t("quality.run.photosPicked", { n: draft.photos.length })
                : t("quality.run.addPhoto")}
            </Button>
          ) : null}
          <p className="qrun-photo-need" data-testid="qrun-photo-need">
            {t("quality.run.photosNeeded", { have: photoCount, need: item.min_photos })}
          </p>
          {draft.photoProblem !== undefined ? (
            <p className="qrun-invalid" role="alert" data-testid="qrun-photo-problem">
              {t("quality.run.photoRefused")}
            </p>
          ) : null}
          {stored !== undefined && stored.photo_file_ids.length > 0 ? (
            <EvidencePhotos fileIds={stored.photo_file_ids} data-testid="qrun-photo-strip" />
          ) : null}
        </div>
      ) : null}

      {editable ? (
        <div className="qrun-item-actions">
          <Button data-testid="qrun-record" disabled={!canRecord} onClick={onRecord}>
            {t("quality.run.record")}
          </Button>
        </div>
      ) : null}

      {draft.settlement !== undefined ? (
        <ItemSettlementNote outcome={draft.settlement} reason={draft.conflictReason} />
      ) : null}
    </li>
  );
}

/**
 * The min–max span with the target tick and the reading's marker.
 *
 * Purely decorative arithmetic (a percentage offset), which is why it is the
 * ONE place in this file that may use floating point: nothing downstream reads
 * it, and a pixel is not a verdict. The VERDICT beside it comes from the exact
 * comparison in `grading.ts`, and the marker is clamped so an out-of-range
 * reading sits at the end of the bar rather than outside it.
 *
 * `left` PLACES the marker and the matching `translateX` PULLS IT BACK BY ITS
 * OWN WIDTH, proportionally: 0% sits flush against the left end, 100% flush
 * against the right, 50% centred on the midpoint. Clamping the position alone
 * was not enough — a reading at or past `max` put a 4px marker's left edge on
 * the track's right edge and painted 2px of it outside, which is real overflow
 * on a row with nothing scrollable around it, and it only showed up on staging
 * because that is where a measurement actually reaches its bound.
 */
function MeasureBar({
  min,
  max,
  target,
  value,
}: {
  min: string;
  max: string;
  target: string | null;
  value: string;
}) {
  const lo = Number(min);
  const hi = Number(max);
  const span = hi - lo;
  const pct = (raw: string | null): number | undefined => {
    if (raw === null || raw.length === 0 || !Number.isFinite(span) || span <= 0) return undefined;
    const n = Number(raw);
    if (!Number.isFinite(n)) return undefined;
    return Math.min(100, Math.max(0, ((n - lo) / span) * 100));
  };
  const targetPct = pct(target);
  const valuePct = pct(value.length > 0 && isExactMeasurement(value) ? value : null);
  return (
    <div className="qrun-bar" data-testid="qrun-bar" aria-hidden="true">
      <span className="qrun-bar-track" />
      {targetPct !== undefined ? (
        <span
          className="qrun-bar-target"
          style={{
            left: `${String(targetPct)}%`,
            transform: `translateX(-${String(targetPct)}%)`,
          }}
        />
      ) : null}
      {valuePct !== undefined ? (
        <span
          className="qrun-bar-mark"
          data-testid="qrun-bar-mark"
          style={{
            left: `${String(valuePct)}%`,
            transform: `translateX(-${String(valuePct)}%)`,
          }}
        />
      ) : null}
    </div>
  );
}

/** The item-level sync marker. A tick and a clock are different glyphs with
 * different words, and `conflicted` is neither of them (D-080). */
function SyncChip({ settlement }: { settlement: OutboxSettlement["outcome"] | undefined }) {
  const { t } = useTranslation();
  if (settlement === undefined) return null;
  if (settlement === "applied") {
    return (
      <StatusChip variant="ok" data-testid="qrun-item-sync-applied">
        {t("quality.run.sync.applied")}
      </StatusChip>
    );
  }
  if (settlement === "queued") {
    return (
      <StatusChip variant="warn" data-testid="qrun-item-sync-queued">
        {t("quality.run.sync.queued")}
      </StatusChip>
    );
  }
  if (settlement === "conflicted") {
    return (
      <StatusChip variant="bad" data-testid="qrun-item-sync-review">
        {t("quality.run.sync.needsReview")}
      </StatusChip>
    );
  }
  if (settlement === "needs_attention") {
    return (
      <StatusChip variant="bad" data-testid="qrun-item-sync-failed">
        {t("quality.run.sync.refused")}
      </StatusChip>
    );
  }
  return (
    <StatusChip variant="neutral" data-testid="qrun-item-sync-superseded">
      {t("quality.run.sync.superseded")}
    </StatusChip>
  );
}

function ItemSettlementNote({
  outcome,
  reason,
}: {
  outcome: OutboxSettlement["outcome"];
  reason: string | undefined;
}) {
  const { t } = useTranslation();
  if (outcome === "applied") return null;
  const bad = outcome === "conflicted" || outcome === "needs_attention";
  return (
    <p
      className={bad ? "qrun-settle-bad" : "qrun-settle-warn"}
      role={bad ? "alert" : "status"}
      data-testid={`qrun-item-settled-${outcome}`}
    >
      {t(`quality.run.settled.${outcome}`)}
      {outcome === "conflicted" && reason !== undefined ? ` (${reason})` : ""}
    </p>
  );
}

/** The completion's own settlement — same five outcomes, same rule: a
 * `conflicted` completion is a review note and never a green tick. */
function SettlementNote({ settlement, testId }: { settlement: OutboxSettlement; testId: string }) {
  const { t } = useTranslation();
  const bad = settlement.outcome === "conflicted" || settlement.outcome === "needs_attention";
  return (
    <p
      className={
        bad
          ? "qrun-settle-bad"
          : settlement.outcome === "queued"
            ? "qrun-settle-warn"
            : "qrun-settle-ok"
      }
      role={bad ? "alert" : "status"}
      data-testid={`${testId}-${settlement.outcome}`}
    >
      {t(`quality.run.completed.${settlement.outcome}`)}
    </p>
  );
}
